import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../utils/api';
import { useWebSocket } from './WebSocketContext';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const { echo, isConnected } = useWebSocket();
  const { addToast } = useToast();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const response = await api.get('/notifications');
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (err) {
      console.warn('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read_at: new Date() } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [user]);

  // WebSocket Listener + Polling Fallback
  useEffect(() => {
    if (!user) return;

    let pollingInterval = null;

    if (echo && isConnected) {
      // Use WebSocket if available and connected
      const channel = echo.private(`App.Models.User.${user.id}`);
      channel.notification((notification) => {
        setNotifications(prev => [notification, ...prev]);
        setUnreadCount(prev => prev + 1);
        addToast(notification.message || 'New update received', 'info');

        // Restore native notification with safety check
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          new Notification(notification.title || 'New Update', {
            body: notification.message || 'You have a new update on Safzi',
            icon: '/Safzi-Logo.png'
          });
        }
      });
    } else {
      // Fallback: Poll every 1.5 seconds if WebSocket is missing/disconnected
      console.log('WS connection lost or unavailable, starting polling (1.5s)...');
      pollingInterval = setInterval(() => {
        // Only poll if tab is visible to save battery/data
        if (!document.hidden) fetchNotifications();
      }, 1500);
    }

    return () => {
      if (echo) echo.leave(`App.Models.User.${user.id}`);
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, [echo, user, isConnected]);

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, markAsRead, fetchNotifications, loading }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);
